import { useEffect, useMemo, useRef, useState } from 'react'
import { useLastCallback } from '$hooks/use-last-callback'

type PauseReason = 'LONG_PRESS' | 'SPACE_BAR' | 'BLUR' | 'MODAL'

interface Props {
  isMediaLoading: boolean
  isLoadingStories: boolean
  isSlideActive: boolean
  hasError: boolean
  uploading: boolean
}

type PauseState = PauseReason[]

export function useStoryPauseController({
  isMediaLoading,
  isLoadingStories,
  isSlideActive,
  hasError,
  uploading,
}: Props) {
  const [pauseState, setPauseState] = useState<PauseReason[]>([])
  const videoPlayerRef = useRef<HTMLVideoElement>(null)

  const handlePauseByReason = useLastCallback((reason: PauseReason) => {
    setPauseState(state => pauseByReason(reason, state))
  })

  const handleTogglePauseByReason = useLastCallback((reason: PauseReason) => {
    setPauseState(state => togglePauseByReason(reason, state))
  })

  const handleUnPauseByReason = useLastCallback((reason: PauseReason) => {
    setPauseState(state => unPauseByReason(reason, state))
  })

  const isPlaybackRequested = useMemo(() => {
    return (
      !isLoadingStories &&
      isSlideActive &&
      !uploading &&
      (isMediaLoading || pauseState.length === 0)
    )
  }, [pauseState, isLoadingStories, isSlideActive, isMediaLoading, uploading])

  const isStoryPlaying = useMemo(() => {
    return (
      isPlaybackRequested &&
      pauseState.length === 0 &&
      !isMediaLoading &&
      !hasError
    )
  }, [pauseState, hasError, isMediaLoading, isPlaybackRequested])

  const isPausedByModal = useMemo(() => {
    return pauseState[pauseState.length - 1] === 'MODAL'
  }, [pauseState])

  const isPausedByLongPress = useMemo(() => {
    return pauseState[pauseState.length - 1] === 'LONG_PRESS'
  }, [pauseState])

  useEffect(() => {
    if (!isPlaybackRequested) {
      videoPlayerRef.current?.pause()
    }
  }, [isPlaybackRequested])

  const markIsPausedByLongPress = useLastCallback(() => {
    handlePauseByReason('LONG_PRESS')
  })

  const unMarkIsPausedByLongPress = useLastCallback(() => {
    handleUnPauseByReason('LONG_PRESS')
  })

  const toggleIsPausedBySpaceBar = useLastCallback(() => {
    handleTogglePauseByReason('SPACE_BAR')
  })

  const unMarkIsPausedBySpaceBar = useLastCallback(() => {
    handleUnPauseByReason('SPACE_BAR')
  })

  const markIsPausedByBlur = useLastCallback(() => {
    handlePauseByReason('BLUR')
  })

  const unMarkIsPausedByBlur = useLastCallback(() => {
    handleUnPauseByReason('BLUR')
  })

  const markIsPausedByModal = useLastCallback(() => {
    handlePauseByReason('MODAL')
  })

  const unMarkIsPausedByModal = useLastCallback(() => {
    handleUnPauseByReason('MODAL')
  })

  return {
    isPlaybackRequested,
    isStoryPlaying,
    videoPlayerRef,

    isPausedByLongPress,
    isPausedByModal,

    markIsPausedByLongPress,
    unMarkIsPausedByLongPress,
    toggleIsPausedBySpaceBar,
    unMarkIsPausedBySpaceBar,
    markIsPausedByBlur,
    unMarkIsPausedByBlur,
    markIsPausedByModal,
    unMarkIsPausedByModal,
  }
}

const pauseByReason = (reason: PauseReason, state: PauseState): PauseState => {
  if (['MODAL'].includes(reason) && !state.includes(reason))
    return [...state, reason]

  if (reason !== 'MODAL' && state.includes('MODAL')) return state

  return [reason]
}

const unPauseByReason = (
  reason: PauseReason,
  state: PauseState,
): PauseState => {
  if (state[state.length - 1] === reason)
    return state.slice(0, state.length - 1)

  return state
}

const togglePauseByReason = (
  reason: PauseReason,
  state: PauseState,
): PauseState => {
  const newState = unPauseByReason(reason, state)
  if (newState !== state) return newState
  return pauseByReason(reason, state)
}
